import React from "react";
import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import TimeService from "src/services/timeService/timeService";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { Subscription } from "@dashboard/devices/types";
import { StatusFeatureTile } from "@dashboard/shared/limits/FeatureTile";
import { FeatureTilesGrid } from "@dashboard/shared/limits/FeatureTilesGrid";
import { SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { SubscriptionAddons } from "@dashboard/shared/subscription/SubscriptionAddons";
import { SubscriptionLimits } from "@dashboard/shared/subscription/SubscriptionLimits";
import { useCancelSubscriptionMutation } from "@dashboard/workspaces/api";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useSubscriptionExpiringWithinMonth,
} from "@dashboard/workspaces/api/helpers";
import { Box, DialogContentText } from "@mui/material";
import { DangerOutlinedButton, PrimaryButton } from "@shared/CustomButton";
import { LoadingBox } from "@shared/Loading";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { SubscriptionAlert } from "../SubscriptionAlert/SubscriptionAlert";

import { useStyles } from "./SubscriptionSection.style";

type Props = {
    subscription: Subscription;
    features: API.Feature[];
    isLoading: boolean;
};
export const SubscriptionSection = (props: Props) => {
    const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = React.useState(false);

    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "pages", "common"]);

    const { data: workspace } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();
    const mayCancelSubscription = workspaceAbility(workspace).can("cancel", "Workspace");
    const isExpired = useCurrentSubscriptionExpired();
    const isExpiringWithinMonth = useSubscriptionExpiringWithinMonth();
    const [cancelSubscription] = useCancelSubscriptionMutation();

    const confirmCancelSubscription = async (id: number) => {
        await cancelSubscription(id)
            .unwrap()
            .then(() => {
                showSuccessToast(t("pages:workspacePage.toast.subscriptionCancelSuccess"));
            })
            .finally(() => setCancelSubscriptionDialogOpen(false));
    };

    const onRenewClick = () => {
        window.open("https://emteria.com/p/subscription", "_blank");
    };

    return (
        <React.Fragment>
            <SectionContainer>
                <SectionHeader>{t("pages:workspacePage.section.title.status")}</SectionHeader>
                <SubscriptionAlert />
                <FeatureTilesGrid>
                    <StatusFeatureTile
                        title={t("pages:workspacePage.tile.badge.chargeId")}
                        content={props.subscription.chargeId}
                    />
                    <StatusFeatureTile title={t("pages:workspacePage.tile.badge.status")} content={props.subscription.status} />
                    <StatusFeatureTile
                        title={t("common:label.expirationDate")}
                        content={
                            props.subscription.expirationDate
                                ? TimeService.getLocalDate(props.subscription.expirationDate, "ISO", "date")
                                : "-"
                        }
                    />
                    <StatusFeatureTile
                        title={t("pages:workspacePage.tile.badge.cancellationDate")}
                        content={
                            props.subscription.cancellationDate
                                ? TimeService.getLocalDate(props.subscription.cancellationDate, "ISO", "date")
                                : "-"
                        }
                    />
                </FeatureTilesGrid>
                <Box className={classes.cancelBtnBox}>
                    <DangerOutlinedButton
                        data-testid="subscriptionSection.btn.cancel"
                        startIcon={<i className="fas fa-ban" />}
                        onClick={() => setCancelSubscriptionDialogOpen(true)}
                        disabled={!mayCancelSubscription || props.subscription.isCancelled}
                        tooltipProps={{
                            hide: mayCancelSubscription && !props.subscription.isCancelled,
                            title: props.subscription.isCancelled
                                ? t("pages:workspacePage.tooltip.subscriptionCanceled")
                                : t("pages:workspacePage.tooltip.noPermissionToCancelSubscription"),
                        }}
                    >
                        {t("pages:workspacePage.button.cancelSubscription")}
                    </DangerOutlinedButton>
                    {isExpired || isExpiringWithinMonth ? (
                        <PrimaryButton data-testid="subscriptionSection.btn.renew" onClick={onRenewClick}>
                            {t("pages:workspacePage.button.renewSubscription")}
                        </PrimaryButton>
                    ) : null}
                </Box>
            </SectionContainer>
            {props.isLoading ? (
                <SectionContainer>
                    <SectionHeader>{t("common:subscription.addons")}</SectionHeader>
                    <LoadingBox height={160} />
                </SectionContainer>
            ) : (
                <SectionContainer>
                    <SubscriptionAddons features={props.features} editable={false} variant="section" />
                </SectionContainer>
            )}

            {props.isLoading ? (
                <SectionContainer>
                    <SectionHeader>{t("common:subscription.limits")}</SectionHeader>
                    <LoadingBox height={160} />
                </SectionContainer>
            ) : (
                <SectionContainer>
                    <SubscriptionLimits features={props.features} editable={false} variant="section" />
                </SectionContainer>
            )}

            <SectionContainer>
                <Box style={{ display: "flex", justifyContent: "flex-end" }}>
                    <PrimaryButton onClick={() => window.open("https://emteria.com/p/subscription", "_blank")}>
                        {t("pages:workspacePage.button.purchaseLimitIncrease")}
                    </PrimaryButton>
                </Box>
            </SectionContainer>

            <ConfirmDialog
                title={t("modals:cancelSubscription.title")}
                open={cancelSubscriptionDialogOpen}
                onConfirm={() => confirmCancelSubscription(props.subscription.id)}
                onClose={() => setCancelSubscriptionDialogOpen(false)}
                dangerButton
            >
                <DialogContentText>{t("modals:cancelSubscription.description")}</DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
